import React, { createContext, ReactNode, useContext, useState } from "react";
import { IProvider } from "interfaces/Provider";
import { IFormError } from "interfaces/ExpressError";
import { IProduct } from "interfaces/Product";
import { IService, IServiceProps } from "interfaces/Service";
import { IUser, IUserToProviderProps } from "interfaces/User";
import { getProviderService } from "services/ProvidersServices";
import { useServiceRegister } from "./ServiceContext";

type ProviderValues = {
  children: ReactNode;
};

type ContextValues = {
  user: IUserToProviderProps;
  provider: IProvider;
  errors: IFormError;
  searching: boolean;
  hasChanges: boolean;
  isPerson: boolean;
  setIsPerson: React.Dispatch<React.SetStateAction<boolean>>;
  setProvider: React.Dispatch<React.SetStateAction<IProvider>>;
  setErrors: React.Dispatch<React.SetStateAction<IFormError>>;
  setUser: React.Dispatch<React.SetStateAction<IUserToProviderProps>>;
  addError: (field: string, message: string) => void;
  removeError: (field: string) => void;
  setHasChanges: React.Dispatch<React.SetStateAction<boolean>>;
  resetProvider: () => void;
  storeProvider: (provider: IProvider) => void;
};

export const ProviderRegisterContext = createContext({} as ContextValues);

export function ProviderRegisterProvider({ children }: ProviderValues) {
  const {services,setServices} = useServiceRegister();
  const [user, setUser] = useState<IUserToProviderProps>({
    type: 2,
  } as IUserToProviderProps);
  const [provider, setProvider] = useState<IProvider>({} as IProvider);
  const [errors, setErrors] = useState<IFormError>({});
  const [isPerson, setIsPerson] = useState<boolean>(false);
  const [searching, setSearching] = useState<boolean>(false);
  const [hasChanges, setHasChanges] = useState<boolean>(false);

  async function storeProvider(providerData: IProvider) {
    setSearching(true);
    return await getProviderService(providerData.id)
      .then((res) => {
        setProvider(res.item);
        const _user = { ...res.item.user };
        if(res.item.services){
        setServices(res.item.services as IServiceProps[]);
      }
        _user.cpf ? setIsPerson(true) : setIsPerson(false);

        setUser({
          cpf: _user.cpf,
          email: _user.email,
          name: _user.name,
          cnpj: _user.cnpj,
          password: _user.password,
          rg: _user.rg,
          type: _user.type,
          id: _user.id,
        });
        setSearching(false);
      })
      .catch((err) => {
        console.log(err);
        setSearching(false);
      });
  }

  function addError(field: string, message: string) {
    const _errors = { ...errors };
    _errors[field] = message;

    setErrors(() => {
      return _errors;
    });
  }

  function removeError(field: string) {
    const _errors = { ...errors };
    delete _errors[field];

    setErrors(() => {
      return _errors;
    });
  }

  function resetProvider() {
    setSearching(true);
    setUser({ type: 2 } as IUserToProviderProps);
    setProvider({} as IProvider);
    setErrors({});
    setSearching(false);
  }

  return (
    <ProviderRegisterContext.Provider
      value={{
        provider,
        setProvider,
        errors,
        setErrors,
        searching,
        user,
        setUser,
        addError,
        removeError,
        hasChanges,
        setHasChanges,
        resetProvider,
        storeProvider,
        isPerson,
        setIsPerson,
      }}
    >
      {children}
    </ProviderRegisterContext.Provider>
  );
}

export const useProviderRegister = () => {
  return useContext(ProviderRegisterContext);
};
